import moment from 'moment';

export default class GraphOptionsBuilder {
  constructor(title, graphWidgetIDs, graphIndex, renderNextGraph) {
    this.xaxis = {};
    this.chart = {
      fontFamily: '"Roboto", sans-serif',
      toolbar: {
        show: true,
        tools: {
          download: false,
          pan: true,
          reset: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
        },
      },
      id: `${title}-${graphIndex}`,
      events: {
        mounted: () => {
          if (graphWidgetIDs.length - 1 <= graphIndex) return;
          renderNextGraph(graphWidgetIDs[graphIndex + 1]);
        },
      },
    };
    this.stroke = { width: 1.5 };
  }
  addLineAndAreaOptions({ yMax, yMin } = {}, signalUnitsMap) {
    this.xaxis = {
      labels: {
        style: {
          fontSize: '10px',
          fontFamily: '"Roboto", sans-serif',
        },
        formatter: (
          val,
          timestamp,
          { w: { config: { series = [] } = {} } = {} }
        ) => {
          const allOutputSignals = series?.every?.(
            ({ signalType } = {}) => signalType === 'outputSignal'
          );
          const now = moment(timestamp);
          return allOutputSignals
            ? [moment.utc(timestamp).format('MM-DD-YYYY')]
            : [now.format('MM-DD-YYYY'), now.format('HH:mm:ss')];
        },
      },
      type: 'datetime',
    };
    const findYMin = (defaultMin) => {
      if (yMin || yMin === 0) return yMin;
      return defaultMin;
    };
    const findYMax = (defaultMax) => {
      if (yMax || yMax === 0) return yMax;
      return defaultMax;
    };

    this.yaxis = {
      min: findYMin(),
      max: findYMax(),
      forceNiceScale: true,
      labels: {
        formatter: (val) => {
          return Number.isInteger(val)
            ? val
            : Number.parseFloat(val.toFixed(2));
        },
      },
    };
    this.dataLabels = { enabled: false };
    this.legend = {
      horizontalAlign: 'center',
      fontSize: '12px',
      fontFamily: '"Roboto", sans-serif',
    };
    this.tooltip = {
      x: {
        formatter: (
          val,
          { seriesIndex, w: { config: { series = [] } = {} } = {} }
        ) => {
          const { signalType } = series?.[seriesIndex] || {};

          return signalType === 'outputSignal'
            ? moment.utc(val).format('MM-DD-YYYY')
            : moment(val).format('MM-DD-YYYY HH:mm:ss');
        },
      },
      y: {
        title: {
          formatter: (seriesName, config) => {
            if (!signalUnitsMap?.size) return `${seriesName}: `;

            const { seriesIndex, w: { config: { series = [] } = {} } = {} } =
                config || {},
              { datapointID } = series?.[seriesIndex] || {},
              { unit } = signalUnitsMap.get(datapointID)?.[seriesName] ?? {};
            if (!unit) return `${seriesName}: `;

            return `${seriesName} (${unit}): `;
          },
        },
      },
    };
    return this;
  }

  addBarOptions(lines, fetchedData, signalUnitsMap, { yMax, yMin } = {}) {
    const minMax = this._getNegativeMinMax(lines, fetchedData);
    if (Number.isFinite(yMax)) minMax.max = yMax;
    if (Number.isFinite(yMin)) minMax.min = yMin;

    this.chart.toolbar.show = false;
    this.dataLabels = this._generateBarDataLabels(
      lines,
      fetchedData,
      false,
      signalUnitsMap
    );
    this.grid = { show: false };
    this.legend = { showForSingleSeries: true };
    this.plotOptions = {
      bar: {
        barHeight: '95%',
        columnWidth: '100%',
        dataLabels: { position: 'center' },
        distributed: true,
        horizontal: true,
      },
    };
    this.stroke = { colors: ['#fff'], width: 3 };
    this.tooltip = this._generateBarTooltip(lines, fetchedData, signalUnitsMap);
    this.xaxis.categories = Array.isArray(lines)
      ? lines.map(({ key }) => key)
      : [];
    this.yaxis = { ...minMax, labels: { show: false } };
    return this;
  }

  addVerticalBarOptions(
    lines,
    fetchedData,
    signalUnitsMap,
    { yMax, yMin } = {}
  ) {
    const minMax = this._getNegativeMinMax(lines, fetchedData);
    if (Number.isFinite(yMax)) minMax.max = yMax;
    if (Number.isFinite(yMin)) minMax.min = yMin;

    this.dataLabels = this._generateBarDataLabels(
      lines,
      fetchedData,
      true,
      signalUnitsMap
    );
    this.legend = {
      fontSize: '10px',
      position: 'bottom',
      showForSingleSeries: true,
    };
    this.plotOptions = {
      bar: {
        barHeight: '100%',
        columnWidth: '90%',
        dataLabels: { orientation: 'horizontal' },
        distributed: true,
      },
    };
    this.stroke = { colors: ['#fff'], width: 2 };
    this.tooltip = this._generateBarTooltip(lines, fetchedData, signalUnitsMap);
    this.xaxis = {
      axisBorder: { show: false },
      axisTicks: { show: false },
      categories: lines?.map?.(({ key }) => key) ?? [],
      labels: { show: false },
    };
    this.yaxis = {
      ...minMax,
      decimalsInFloat: 0,
      tickAmount: 6,
    };
    return this;
  }

  _formatBarTooltipX(val, dataPointIndex, lines, fetchedData) {
    const { datapointID, type } = lines[dataPointIndex] || {};
    const [{ createdAt } = {}] = fetchedData.get(datapointID) || [];
    return !createdAt
      ? ''
      : type === 'outputSignal'
        ? `updated at ${moment.utc(createdAt).format('MM-DD-YYYY')}`
        : `updated at ${moment(createdAt).format('MM-DD-YYYY HH:mm')}`;
  }

  _formatBarTooltipY(
    val,
    dataPointIndex,
    labels,
    lines,
    fetchedData,
    signalUnitsMap
  ) {
    const { key, datapointID } = lines[dataPointIndex];
    const [{ signals = {} } = {}] = fetchedData.get(datapointID) || [];
    let uom = '';
    if (Object.keys(signals || {})?.length) {
      ({ uom = '' } =
        Object.values(signals).find(({ name }) => name === key) || {});
    }

    const { display_precision, unit } =
      signalUnitsMap.get(datapointID)?.[key] || {};
    const value = Number.isInteger(val)
      ? val
      : Number(val).toFixed(display_precision ?? 2);

    return `${labels[dataPointIndex]} ${value} ${unit || uom}`;
  }

  _generateBarDataLabels(lines, fetchedData, isVertical, signalUnitsMap) {
    return {
      formatter: (val, { dataPointIndex }) => {
        const { key: name, datapointID } = lines[dataPointIndex] || {};
        const [{ signals = {} } = {}] = fetchedData.get(datapointID) || [];
        let rawValue = val,
          uom = '';
        if (Object.keys(signals || {})?.length) {
          ({ uom = '', value: rawValue } =
            Object.values(signals).find(({ name: n }) => n === name) || {});
        }

        const { display_precision, unit } =
          signalUnitsMap.get(datapointID)?.[name] ?? {};

        const value = Number.isInteger(val)
          ? val
          : Number(val).toFixed(display_precision ?? 2);

        return value === 'NaN' ? rawValue : `${value} ${unit || uom}`;
      },
      offsetY: isVertical ? -2 : 5,
      style: {
        colors: ['#686868'],
        fontFamily: '"Roboto", sans-serif',
        fontSize: '0.75rem',
      },
      textAnchor: isVertical ? 'middle' : 'start',
    };
  }
  _generateBarTooltip(lines, fetchedData, signalUnitsMap) {
    return {
      style: { fontFamily: '"Roboto", sans-serif', fontSize: '10px' },
      x: {
        formatter: (val, { dataPointIndex }) => {
          return this._formatBarTooltipX(
            val,
            dataPointIndex,
            lines,
            fetchedData
          );
        },
      },
      y: {
        formatter: (
          val,
          { dataPointIndex, w: { globals: { labels = [] } = {} } = {} }
        ) => {
          return this._formatBarTooltipY(
            val,
            dataPointIndex,
            labels,
            lines,
            fetchedData,
            signalUnitsMap
          );
        },
        title: { formatter: () => '' },
      },
    };
  }

  _getNegativeMinMax(lines = [], fetchedData = new Map()) {
    return lines.reduce((minMax, { datapointID = '', key = '' } = {}) => {
      const [{ signals = {} } = {}] = fetchedData.get(datapointID) || [];
      if (!Object.keys(signals || {}).length) return minMax;

      const { value } =
        Object.values(signals).find(({ name } = {}) => name === key) || {};
      if (!value) return minMax;

      if (value > 0) {
        if (!minMax.max || minMax.max < value) {
          minMax.max = value;
        }
      }

      if (value < 0) {
        if (!minMax.min || minMax.min > value) {
          minMax.min = value;
        }
      }

      return minMax;
    }, {});
  }

  addPieChartOptions(lines, fetchedData, signalUnitsMap) {
    const unitsMap = new Map();
    this.labels = lines.reduce((l, { datapointID, key }) => {
      const [{ signals } = {}] = fetchedData.get(datapointID) ?? [];
      if (!Object.keys(signals || {})?.length) return l;

      const {
        name,
        uom = '',
        value,
      } = Object.values(signals).find(({ name }) => name === key) || {};
      if (!value && value !== 0) return l;

      const { display_precision, unit } =
        signalUnitsMap.get(datapointID)?.[key] || {};

      unitsMap.set(name, { display_precision, uom: unit || uom });
      return l.concat(key);
    }, []);
    this.dataLabels = {
      enabled: true,
      formatter: (percentage, opts) => {
        return this._formatPieDataLabels(opts, unitsMap);
      },
      style: { fontSize: '10px' },
    };
    this.legend = { fontSize: '10px', position: 'bottom' };
    this.tooltip = {
      custom: (opts) => {
        const {
          seriesIndex,
          w: {
            globals: { colors = [] },
          },
        } = opts;
        return `
          <div class="arrow_box" style="background-color:${
            colors[seriesIndex]
          };padding:5px">
            <span>${this._formatPieDataLabels(opts, unitsMap)}</span>
          </div>
        `;
      },
    };
    return this;
  }

  _formatPieDataLabels({ seriesIndex, w }, units = new Map()) {
    const rawValue = w?.config?.series?.[seriesIndex];
    if (!rawValue && rawValue !== 0) return;

    const { display_precision, uom } =
      units.get(w?.config?.labels?.[seriesIndex]) ?? {};

    switch (true) {
      case typeof rawValue === 'number':
        return `${
          Number.isInteger(rawValue)
            ? rawValue
            : rawValue.toFixed(display_precision ?? 2)
        } ${uom}`;
      case typeof rawValue === 'string': {
        const [int, dec] = rawValue.split('.');
        return `${int}.${dec.slice(0, display_precision ?? 2)} ${uom}`;
      }
      default:
        return;
    }
  }
}
